import React, { useEffect, useState } from 'react';
import { ArrowDown, Github, Linkedin, Twitter, Mail, Download, ExternalLink, Camera } from 'lucide-react';
import { personalInfo, socialLinks } from '../mock';

const iconMap = { Github, Linkedin, Twitter, Mail };

/* ─────────────────────────────────────────────────────────
   PARALLAX BACKGROUND LAYERS
   Three SVG layers that move at different scroll speeds,
   creating genuine depth perception.
   ───────────────────────────────────────────────────────── */

/* Layer 1 — Far (slowest, 0.15x) : large hollow geometry */
const LayerFar = () => (
  <svg
    width="100%" height="100%"
    viewBox="0 0 1440 900"
    preserveAspectRatio="xMidYMid slice"
    style={{ position: 'absolute', inset: 0 }}
  >
    <defs>
      <radialGradient id="bgGlow1" cx="65%" cy="35%" r="55%">
        <stop offset="0%" stopColor="#14b8a6" stopOpacity="0.07" />
        <stop offset="100%" stopColor="#14b8a6" stopOpacity="0" />
      </radialGradient>
      <radialGradient id="bgGlow2" cx="20%" cy="75%" r="45%">
        <stop offset="0%" stopColor="#06b6d4" stopOpacity="0.05" />
        <stop offset="100%" stopColor="#06b6d4" stopOpacity="0" />
      </radialGradient>
    </defs>

    {/* Base ambient glow — not just solid black */}
    <rect width="1440" height="900" fill="url(#bgGlow1)" />
    <rect width="1440" height="900" fill="url(#bgGlow2)" />

    {/* Large hollow hexagon — top-right */}
    <polygon
      points="1200,60 1340,140 1340,300 1200,380 1060,300 1060,140"
      fill="none"
      stroke="#14b8a6"
      strokeWidth="1"
      strokeOpacity="0.09"
    />
    {/* Inner smaller hexagon */}
    <polygon
      points="1200,110 1300,165 1300,275 1200,330 1100,275 1100,165"
      fill="none"
      stroke="#14b8a6"
      strokeWidth="0.5"
      strokeOpacity="0.06"
    />

    {/* Large arc — bottom-left */}
    <path
      d="M -60 700 A 400 400 0 0 1 340 340"
      fill="none"
      stroke="#14b8a6"
      strokeWidth="1"
      strokeOpacity="0.08"
    />
    <path
      d="M -80 720 A 440 440 0 0 1 360 320"
      fill="none"
      stroke="#06b6d4"
      strokeWidth="0.5"
      strokeOpacity="0.05"
    />

    {/* Horizontal bar cluster — mid-left */}
    {[0, 18, 36, 54].map((offset, i) => (
      <line
        key={i}
        x1="40"
        y1={420 + offset}
        x2={160 + i * 20}
        y2={420 + offset}
        stroke="#14b8a6"
        strokeWidth="1"
        strokeOpacity={0.08 - i * 0.015}
      />
    ))}

    {/* Large circle outline — center-right */}
    <circle
      cx="1100"
      cy="620"
      r="200"
      fill="none"
      stroke="#06b6d4"
      strokeWidth="1"
      strokeOpacity="0.06"
      strokeDasharray="8 16"
    />
    <circle
      cx="1100"
      cy="620"
      r="130"
      fill="none"
      stroke="#14b8a6"
      strokeWidth="0.5"
      strokeOpacity="0.045"
    />
  </svg>
);

/* Layer 2 — Mid (medium, 0.32x) : circuit fragments, nodes */
const LayerMid = () => (
  <svg
    width="100%" height="100%"
    viewBox="0 0 1440 900"
    preserveAspectRatio="xMidYMid slice"
    style={{ position: 'absolute', inset: 0 }}
  >
    {/* Circuit-trace lines */}
    <path
      d="M 200 150 L 200 280 L 380 280 L 380 200 L 520 200"
      fill="none" stroke="#14b8a6" strokeWidth="1" strokeOpacity="0.12"
    />
    <circle cx="200" cy="150" r="4" fill="#14b8a6" fillOpacity="0.18" />
    <circle cx="380" cy="280" r="3" fill="#14b8a6" fillOpacity="0.14" />
    <circle cx="520" cy="200" r="5" fill="none" stroke="#14b8a6" strokeWidth="1" strokeOpacity="0.18" />

    {/* Diagonal slash line — top-right area */}
    <path
      d="M 900 40 L 1060 160 L 1060 240"
      fill="none" stroke="#06b6d4" strokeWidth="1" strokeOpacity="0.1"
    />
    <circle cx="900" cy="40" r="3" fill="#06b6d4" fillOpacity="0.15" />
    <circle cx="1060" cy="240" r="4" fill="none" stroke="#06b6d4" strokeWidth="1" strokeOpacity="0.15" />

    {/* Bracket shape — right mid */}
    <path
      d="M 1360 300 L 1320 300 L 1320 580 L 1360 580"
      fill="none" stroke="#14b8a6" strokeWidth="1" strokeOpacity="0.1"
    />

    {/* Small square diamond — far right, not overlapping content */}
    <rect
      x="1260" y="380"
      width="40" height="40"
      fill="none" stroke="#14b8a6" strokeWidth="1" strokeOpacity="0.12"
      transform="rotate(45, 1280, 400)"
    />

    {/* Floating triangles — away from content zone */}
    <polygon
      points="80,640 140,550 200,640"
      fill="none" stroke="#06b6d4" strokeWidth="1" strokeOpacity="0.1"
    />
    <polygon
      points="1150,180 1190,120 1230,180"
      fill="none" stroke="#14b8a6" strokeWidth="0.8" strokeOpacity="0.1"
    />

    {/* Bottom horizontal trace */}
    <path
      d="M 500 820 L 700 820 L 700 780 L 860 780"
      fill="none" stroke="#14b8a6" strokeWidth="1" strokeOpacity="0.1"
    />
    <circle cx="860" cy="780" r="3.5" fill="none" stroke="#14b8a6" strokeWidth="1" strokeOpacity="0.15" />

    {/* Plus/crosshair marks */}
    {[[120, 500], [780, 160], [1280, 450]].map(([cx, cy], i) => (
      <g key={i} opacity={0.13}>
        <line x1={cx - 8} y1={cy} x2={cx + 8} y2={cy} stroke="#14b8a6" strokeWidth="1" />
        <line x1={cx} y1={cy - 8} x2={cx} y2={cy + 8} stroke="#14b8a6" strokeWidth="1" />
        <circle cx={cx} cy={cy} r="3" fill="none" stroke="#14b8a6" strokeWidth="0.8" />
      </g>
    ))}
  </svg>
);

/* Layer 3 — Near (fastest, 0.55x) : scattered dots + fine details */
const LayerNear = () => (
  <svg
    width="100%" height="100%"
    viewBox="0 0 1440 900"
    preserveAspectRatio="xMidYMid slice"
    style={{ position: 'absolute', inset: 0 }}
  >
    {/* Dot cluster — top-left */}
    {[
      [60, 80], [80, 100], [100, 80], [120, 100], [80, 120],
      [140, 80], [60, 120], [140, 120], [100, 60], [120, 140]
    ].map(([x, y], i) => (
      <circle
        key={i}
        cx={x} cy={y} r={i % 3 === 0 ? 1.5 : 1}
        fill="#14b8a6"
        fillOpacity={0.2 - (i % 4) * 0.04}
      />
    ))}

    {/* Dot cluster — bottom-right */}
    {[
      [1340, 800], [1360, 820], [1380, 800], [1360, 780],
      [1320, 820], [1400, 820], [1340, 840], [1380, 840]
    ].map(([x, y], i) => (
      <circle
        key={`br-${i}`}
        cx={x} cy={y} r={i % 2 === 0 ? 1.5 : 1}
        fill="#06b6d4"
        fillOpacity={0.18 - (i % 3) * 0.03}
      />
    ))}

    {/* Scattered single dots */}
    {[
      [450, 120, 1.2, 0.18],
      [820, 60, 1.5, 0.15],
      [1180, 320, 1.2, 0.14],
      [260, 720, 1, 0.12],
      [940, 830, 1.5, 0.16],
      [1420, 200, 1.2, 0.12],
      [680, 780, 1, 0.13],
      [40, 400, 1.5, 0.11],
      [1100, 80, 1.2, 0.15],
    ].map(([x, y, r, op], i) => (
      <circle key={`s-${i}`} cx={x} cy={y} r={r} fill="#14b8a6" fillOpacity={op} />
    ))}

    {/* Fine slash marks */}
    {[[600, 300, 20], [300, 440, 16], [1050, 700, 20]].map(([x, y, len], i) => (
      <line
        key={`sl-${i}`}
        x1={x} y1={y} x2={x + len} y2={y - len * 0.7}
        stroke="#14b8a6" strokeWidth="0.8" strokeOpacity="0.15"
      />
    ))}
  </svg>
);

/* ─────────────────────────────────────────────────────────
   HERO COMPONENT
   ───────────────────────────────────────────────────────── */
const Hero = () => {
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });
  const [scrollY, setScrollY] = useState(0);
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    const handleMouse = (e) => {
      const x = (e.clientX / window.innerWidth - 0.5) * 30;
      const y = (e.clientY / window.innerHeight - 0.5) * 30;
      setMousePos({ x, y });
    };

    const handleScroll = () => setScrollY(window.scrollY);

    window.addEventListener('mousemove', handleMouse, { passive: true });
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      window.removeEventListener('mousemove', handleMouse);
      window.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const scrollToWork = () => {
    const el = document.getElementById('work');
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  };

  /* Per-layer parallax transforms — scroll + mouse, different rates */
  const layerFarStyle = {
    position: 'absolute', inset: 0, zIndex: 0, pointerEvents: 'none', overflow: 'hidden',
    transform: `translate(${mousePos.x * 0.08}px, ${mousePos.y * 0.08 + scrollY * 0.15}px)`,
    transition: 'transform 0.1s linear',
    willChange: 'transform'
  };

  const layerMidStyle = {
    position: 'absolute', inset: 0, zIndex: 1, pointerEvents: 'none', overflow: 'hidden',
    transform: `translate(${mousePos.x * 0.2}px, ${mousePos.y * 0.2 + scrollY * 0.32}px)`,
    transition: 'transform 0.08s linear',
    willChange: 'transform'
  };

  const layerNearStyle = {
    position: 'absolute', inset: 0, zIndex: 2, pointerEvents: 'none', overflow: 'hidden',
    transform: `translate(${mousePos.x * 0.38}px, ${mousePos.y * 0.38 + scrollY * 0.55}px)`,
    transition: 'transform 0.06s linear',
    willChange: 'transform'
  };

  return (
    <section
      id="home"
      className="portfolio-section relative min-h-screen flex flex-col justify-center overflow-hidden"
      style={{ paddingTop: '4rem' }}
    >
      {/* ── Parallax Background System ── */}
      <div style={{ position: 'absolute', inset: 0, zIndex: 0, overflow: 'hidden' }}>
        <div style={layerFarStyle}>
          <LayerFar />
        </div>
        <div style={layerMidStyle}>
          <LayerMid />
        </div>
        <div style={layerNearStyle}>
          <LayerNear />
        </div>
      </div>

      {/* Main content — two-column layout */}
      <div className="relative z-10 max-w-6xl mx-auto px-5 sm:px-8 py-12 lg:py-20 w-full">
        <div className="flex flex-col lg:flex-row items-center gap-10 lg:gap-20">

          {/* ── Left: Text Content ── */}
          <div className="flex-1 flex flex-col gap-6 min-w-0 items-center lg:items-start text-center lg:text-left w-full">

            <div className="hero-item-1">
              <span className="available-badge">
                <span className="available-dot" />
                Available for new projects
              </span>
            </div>

            <div className="hero-item-2">
              <h1
                className="font-bold tracking-tight"
                style={{
                  fontSize: 'clamp(2.4rem, 5.5vw, 4.8rem)',
                  lineHeight: 1.06,
                  color: 'var(--text-primary)'
                }}
              >
                Hi, I'm{' '}
                <span className="accent-text">{personalInfo.name}</span>
              </h1>
              <h2
                className="mt-3 font-medium"
                style={{
                  fontSize: 'clamp(1rem, 2.5vw, 1.3rem)',
                  color: 'var(--text-secondary)',
                  lineHeight: 1.5
                }}
              >
                {personalInfo.title}
              </h2>
            </div>

            <p
              className="hero-item-3"
              style={{
                fontSize: 'clamp(0.9rem, 1.6vw, 1.05rem)',
                color: 'var(--text-secondary)',
                lineHeight: 1.8,
                maxWidth: '480px'
              }}
            >
              {personalInfo.tagline}
            </p>

            <div className="flex flex-wrap gap-3 hero-item-4 justify-center lg:justify-start">
              <button
                onClick={scrollToWork}
                className="btn-accent gap-2 px-6 py-3 text-sm font-semibold"
                style={{ border: 'none', cursor: 'pointer' }}
              >
                View My Work
                <ExternalLink size={14} />
              </button>
              <a
                href={personalInfo.resumeUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-ghost-accent gap-2 px-6 py-3 text-sm"
                style={{ textDecoration: 'none' }}
              >
                <Download size={14} />
                Download CV
              </a>
            </div>

            <div className="flex flex-wrap items-center gap-3 pt-1 hero-item-5 justify-center lg:justify-start">
              {socialLinks.map((link) => {
                const Icon = iconMap[link.icon];
                if (!Icon) return null;
                return (
                  <a
                    key={link.label}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    title={link.label}
                    className="social-icon-btn"
                  >
                    <Icon size={16} />
                  </a>
                );
              })}
              <div style={{ width: '1px', height: '22px', background: 'var(--border-color)', margin: '0 2px', flexShrink: 0 }} />
              <span className="text-xs mono" style={{ color: 'var(--text-muted)' }}>
                {personalInfo.location}
              </span>
              <div className="hidden sm:block" style={{ width: '1px', height: '22px', background: 'var(--border-color)', margin: '0 2px', flexShrink: 0 }} />
              <a
                href={`mailto:${personalInfo.email}`}
                className="text-xs mono hidden sm:inline"
                style={{ color: 'var(--text-muted)', textDecoration: 'none' }}
              >
                {personalInfo.email}
              </a>
            </div>

            {/* stats hidden */}
          </div>

          {/* ── Right: Profile Photo ── */}
          <div
            className="hero-item-2 flex-shrink-0 flex items-center justify-center order-first lg:order-last"
            style={{ width: 'clamp(200px, 50vw, 380px)' }}
          >
            <div className="hero-photo-wrap">
              <div className="hero-photo-ring" />
              <div className="hero-photo-dots" />
              <div className="hero-photo-inner">
                {!imgError && personalInfo.profileImage ? (
                  <img
                    src={personalInfo.profileImage}
                    alt={personalInfo.name}
                    onError={() => setImgError(true)}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center top',
                      display: 'block'
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: '100%', height: '100%',
                      display: 'flex', flexDirection: 'column',
                      alignItems: 'center', justifyContent: 'center',
                      gap: '12px', background: 'var(--bg-secondary)'
                    }}
                  >
                    <div style={{
                      width: '64px', height: '64px', borderRadius: '50%',
                      background: 'var(--tag-bg)', border: '2px solid var(--tag-border)',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      color: 'var(--accent)'
                    }}>
                      <Camera size={26} />
                    </div>
                    <p className="text-xs mono text-center px-4" style={{ color: 'var(--text-muted)', lineHeight: 1.5 }}>
                      Update <code style={{ color: 'var(--accent)' }}>profileImage</code><br />in mock.js
                    </p>
                  </div>
                )}
              </div>
              <div className="hero-photo-chip">
                <span className="available-dot" style={{ width: '5px', height: '5px' }} />
                <span className="mono" style={{ fontSize: '0.68rem', fontWeight: 600, color: 'var(--accent)' }}>
                  Open to Work
                </span>
              </div>
            </div>
          </div>

        </div>
      </div>

      {/* Scroll indicator */}
      <button
        onClick={scrollToWork}
        className="absolute bottom-8 left-1/2 scroll-indicator flex flex-col items-center gap-1.5 hero-item-5"
        style={{
          background: 'none', border: 'none', cursor: 'pointer',
          color: 'var(--text-muted)', transform: 'translateX(-50%)'
        }}
        aria-label="Scroll down"
      >
        <span className="text-xs mono" style={{ letterSpacing: '0.1em' }}>scroll</span>
        <ArrowDown size={13} />
      </button>
    </section>
  );
};

export default Hero;
